import React, { useState } from 'react';
import { Box, Pressable, Text } from '@gluestack-ui/themed';
import { Filter } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

import { ReferenceListField } from '@/admin/common/references';
import type { ReferenceListValue } from '@/admin/common/references';
import { SPARKLINE_SCORE_STROKE_BOTTOM } from '@/admin/dashboards/sparklineSeries';

const INK_MUTED = '#98A2B3';
const CHART_BLUE = SPARKLINE_SCORE_STROKE_BOTTOM;

export type ModuleFilterMenuProps = {
  value: ReferenceListValue;
  onChange: (value: ReferenceListValue) => void;
  disabled?: boolean;
};

/**
 * Funnel at the top right. The menu is the existing module search, so more
 * filters can be added beside it later.
 */
export function ModuleFilterMenu({ value, onChange, disabled = false }: ModuleFilterMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const active = value.length > 0;

  return (
    <Box position="relative" flexShrink={0}>
      <Pressable
        testID="dashboard-module-filter"
        accessibilityRole="button"
        accessibilityLabel={t('admin.reports.filters.filterSettings')}
        accessibilityState={{ expanded: open, selected: active }}
        disabled={disabled}
        onPress={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
        width={36}
        height={36}
        alignItems="center"
        justifyContent="center"
        borderWidth={1}
        borderColor={active ? CHART_BLUE : '$borderLight'}
        borderRadius="$lg"
        bg="$bgCard"
        opacity={disabled ? 0.5 : 1}
      >
        <Filter
          size={16}
          strokeWidth={1.75}
          color={active ? CHART_BLUE : INK_MUTED}
        />
      </Pressable>
      {active ? (
        <Box
          testID="dashboard-module-filter-count"
          position="absolute"
          top={-6}
          right={-6}
          minWidth={16}
          height={16}
          px="$1"
          borderRadius="$full"
          alignItems="center"
          justifyContent="center"
          borderWidth={2}
          borderColor="$bgCard"
          pointerEvents="none"
          style={{ backgroundColor: CHART_BLUE }}
        >
          <Text
            testID="dashboard-module-filter-count-value"
            fontSize={10}
            lineHeight={12}
            fontWeight="600"
            color="#FFFFFF"
          >
            {String(value.length)}
          </Text>
        </Box>
      ) : null}
      {!!open && (
        <Box
          testID="dashboard-module-filter-menu"
          position="absolute"
          top="100%"
          right={0}
          mt="$1"
          zIndex={40}
          bg="$bgCard"
          borderWidth={1}
          borderColor="$borderLight"
          borderRadius="$sm"
          p="$3"
          softShadow="3"
          width={320}
        >
          <Text
            testID="dashboard-module-filter-title"
            mb="$2"
            fontSize={13}
            lineHeight={18}
            fontWeight="600"
            color="$textHeading"
          >
            {t('admin.reports.filters.filterSettings')}
          </Text>
          <ReferenceListField
            testID="dashboard-module-filter-modules"
            value={value}
            onChange={onChange}
            objectName="modules"
            fixedFilter={{ state: 'published' }}
            searchField="title"
            limit={8}
            searchPlaceholderKey="admin.course.enrollments.extras.searchPlaceholder"
          />
        </Box>
      )}
    </Box>
  );
}
